/**
 * Iter XX.114 — Founder-only Player Understanding observability.
 *
 * Renders the JSON from `GET /api/admin/player-insights?user_id=…`
 * as a minimal table. No design polish yet — the page exists so the
 * founder can inspect the model without curl.
 *
 * Server gates this data behind:
 *   · admin auth (`_require_admin`)
 *   · `COACHING_DEBUG_ENABLED=1` env
 * so this component only needs to worry about the query flow and
 * a readable table.
 */
import React, { useEffect, useState } from "react";
import { credentialsMode } from "../lib/nativePlatform";

const API = process.env.REACT_APP_BACKEND_URL;

function bandTone(band) {
  if (band === "high") return "text-emerald-300";
  if (band === "moderate") return "text-amber-300";
  return "text-neutral-400";
}

function StatusPill({ status }) {
  const tone =
    status === "active" ? "bg-emerald-900/40 text-emerald-200"
    : status === "contradicted" ? "bg-rose-900/40 text-rose-200"
    : "bg-neutral-800 text-neutral-400";
  return (
    <span
      data-testid={`insight-status-${status}`}
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}
    >
      {status}
    </span>
  );
}

export default function AdminInsights() {
  const [subjectKind, setSubjectKind] = useState("user_id");
  const [subjectValue, setSubjectValue] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Player Understanding — Founder Debug";
  }, []);

  const fetchInsights = async () => {
    if (!subjectValue.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = new URLSearchParams();
      params.set(subjectKind, subjectValue.trim());
      const res = await fetch(
        `${API}/api/admin/player-insights?${params.toString()}`,
        { credentials: credentialsMode() },
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status}: ${txt.slice(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const renderRows = (rows) =>
    (rows || []).map((r) => (
      <tr
        key={r.insight_id}
        data-testid={`insight-row-${r.insight_id}`}
        className="border-b border-neutral-800/60"
      >
        <td className="py-2 pr-3 text-neutral-500 text-[11px] font-mono">
          {r.insight_id}
        </td>
        <td className="py-2 pr-3 text-neutral-200">
          <div className="text-[13px] leading-snug">{r.statement}</div>
        </td>
        <td className="py-2 pr-3 text-neutral-400 text-[12px]">
          {r.category}
        </td>
        <td className={`py-2 pr-3 text-[12px] font-medium ${bandTone(r.confidence_band)}`}>
          {r.confidence_band}
        </td>
        <td className="py-2 pr-3 text-[12px] text-neutral-300 text-right">
          {r.evidence_count}
        </td>
        <td className="py-2 pr-3 text-[12px] text-neutral-500">
          {(r.last_observed_at || "").slice(0, 16).replace("T", " ")}
        </td>
        <td className="py-2 pr-3">
          <StatusPill status={r.status} />
        </td>
        <td className="py-2 pr-3 text-[11px] text-neutral-500">
          {(r.supporting_session_ids || []).slice(-3).join(", ")}
        </td>
      </tr>
    ));

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 px-6 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <header
          data-testid="admin-insights-header"
          className="flex items-baseline justify-between"
        >
          <h1 className="text-xl font-semibold">
            Player Understanding — Founder Debug
          </h1>
          <span className="text-[11px] text-neutral-500 font-mono">
            Iter XX.114
          </span>
        </header>

        <section
          data-testid="admin-insights-controls"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/50 p-3"
        >
          <select
            value={subjectKind}
            onChange={(e) => setSubjectKind(e.target.value)}
            data-testid="admin-insights-kind-select"
            className="rounded bg-neutral-800 text-[13px] px-2 py-1.5 border border-neutral-700"
          >
            <option value="user_id">user_id</option>
            <option value="session_id">session_id</option>
          </select>
          <input
            value={subjectValue}
            onChange={(e) => setSubjectValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchInsights(); }}
            placeholder={
              subjectKind === "user_id"
                ? "u_… (signed-in user)"
                : "wsess_… (anonymous session)"
            }
            data-testid="admin-insights-subject-input"
            className="flex-1 min-w-[280px] rounded bg-neutral-800 text-[13px] px-2 py-1.5 border border-neutral-700 focus:outline-none focus:border-emerald-700"
          />
          <button
            onClick={fetchInsights}
            disabled={loading || !subjectValue.trim()}
            data-testid="admin-insights-fetch-btn"
            className="rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-neutral-950 text-[13px] font-medium px-3 py-1.5"
          >
            {loading ? "Loading…" : "Load"}
          </button>
        </section>

        {error && (
          <div
            data-testid="admin-insights-error"
            className="rounded border border-rose-900/60 bg-rose-950/40 text-rose-300 text-[13px] p-3"
          >
            {error}
          </div>
        )}

        {data && (
          <>
            <div className="text-[13px] text-neutral-400">
              subject_key: <span className="font-mono text-neutral-200">{data.subject_key || "—"}</span>
              &nbsp;·&nbsp;
              active: <span data-testid="admin-insights-active-count">{(data.active || []).length}</span>
              &nbsp;·&nbsp;
              contradicted: <span data-testid="admin-insights-contradicted-count">{(data.contradicted || []).length}</span>
              &nbsp;·&nbsp;
              retired: <span data-testid="admin-insights-retired-count">{(data.retired || []).length}</span>
            </div>

            <section
              data-testid="admin-insights-active-section"
              className="rounded-lg border border-neutral-800 bg-neutral-900/40 overflow-x-auto"
            >
              <div className="px-3 py-2 text-[12px] uppercase tracking-widest text-neutral-500 border-b border-neutral-800">
                Active
              </div>
              <table className="w-full text-left">
                <thead className="text-[11px] uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="pl-3 py-2 pr-3">insight_id</th>
                    <th className="py-2 pr-3">statement</th>
                    <th className="py-2 pr-3">category</th>
                    <th className="py-2 pr-3">band</th>
                    <th className="py-2 pr-3 text-right">obs</th>
                    <th className="py-2 pr-3">last_observed</th>
                    <th className="py-2 pr-3">status</th>
                    <th className="py-2 pr-3">sessions</th>
                  </tr>
                </thead>
                <tbody>{renderRows(data.active)}</tbody>
              </table>
            </section>

            {(data.contradicted || []).length > 0 && (
              <section
                data-testid="admin-insights-contradicted-section"
                className="rounded-lg border border-rose-900/40 bg-rose-950/10 overflow-x-auto"
              >
                <div className="px-3 py-2 text-[12px] uppercase tracking-widest text-rose-300 border-b border-rose-900/40">
                  Contradicted
                </div>
                <table className="w-full text-left">
                  <thead className="text-[11px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="pl-3 py-2 pr-3">insight_id</th>
                      <th className="py-2 pr-3">statement</th>
                      <th className="py-2 pr-3">category</th>
                      <th className="py-2 pr-3">band</th>
                      <th className="py-2 pr-3 text-right">obs</th>
                      <th className="py-2 pr-3">last_observed</th>
                      <th className="py-2 pr-3">status</th>
                      <th className="py-2 pr-3">sessions</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows(data.contradicted)}</tbody>
                </table>
              </section>
            )}
          </>
        )}

        {!data && !error && (
          <div className="text-[13px] text-neutral-500">
            Enter a subject and press Load.
          </div>
        )}
      </div>
    </div>
  );
}
